import { useEffect } from "react";
import { CSSObject } from "@emotion/react";
import { IonItem } from "@ionic/react";
import { ChainedCommands, Editor } from "@tiptap/core";
import { Placeholder } from "@tiptap/extensions";
import { Markdown as MarkdownExtension } from "@tiptap/markdown";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { IconType } from "react-icons";
import { FaBold, FaHeading, FaItalic, FaListOl, FaListUl } from "react-icons/fa";
import Markdown from "react-markdown";

import { translate } from "~utils";

import styles from "./EditableMarkdown.module.css";
import { EditableFieldsBase } from "./types";

/***** Styles *****/
const editorStyles = {
  ".ProseMirror": {
    minHeight: 120,
    padding: "8px 12px",
    border: "1px solid var(--swing-gray-200)",
    borderRadius: 4,
    outline: "none",
    "p, li": { lineHeight: 1.5 },
    "p.is-editor-empty:first-of-type::before": {
      content: "attr(data-placeholder)",
      color: "var(--swing-gray-300)",
      float: "left",
      height: 0,
      pointerEvents: "none",
    },
  },
} satisfies CSSObject;

type EditableMarkdownProps = EditableFieldsBase & {
  isInset?: boolean;
  autoFocus?: boolean;
  onChange: (value: string) => void;
};

export const EditableMarkdown: React.FC<EditableMarkdownProps> = ({
  isEditing,
  value,
  label,
  placeholder,
  onChange,
  className,
  lines,
  isInset,
  autoFocus = true,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      MarkdownExtension,
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    content: value ?? "",
    contentType: "markdown",
    autofocus: autoFocus,
    editable: isEditing,
    editorProps: { attributes: { class: styles.markdownViewer } },
    onUpdate: ({ editor }) => onChange(editor.getMarkdown()),
  });

  // Pull in value changes that came from outside the editor (form resets, refetches)
  useEffect(() => {
    if (!editor || (value ?? "") === editor.getMarkdown()) return;

    editor.commands.setContent(value ?? "", { contentType: "markdown", emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(isEditing);
  }, [editor, isEditing]);

  return (
    <IonItem className={`${className} w-full`} lines={lines}>
      {isEditing ? (
        <div className="w-full" css={editorStyles}>
          {label && <p className={`font-medium ${isInset && "mt-2 mb-1"}`}>{label}</p>}
          {editor && <Toolbar editor={editor} />}
          <EditorContent editor={editor} />
        </div>
      ) : (
        <div className="flex w-full gap-4 pb-4">
          <div className={isInset ? "px-4" : ""}>
            <p className={`font-medium ${isInset && "mt-2 mb-1"}`}>{label}</p>
            {value ? (
              <Markdown className={styles.markdownViewer}>{value}</Markdown>
            ) : (
              <span className="italic">{translate("NOT_PROVIDED")}</span>
            )}
          </div>
        </div>
      )}
    </IonItem>
  );
};

/***** Helper Components *****/
type ToolbarItem = [string, IconType, string, (chain: ChainedCommands) => ChainedCommands];

function Toolbar({ editor }: { editor: Editor }) {
  const items: ToolbarItem[] = [
    ["bold", FaBold, translate("MARKDOWN_BOLD"), (c) => c.toggleBold()],
    ["italic", FaItalic, translate("MARKDOWN_ITALIC"), (c) => c.toggleItalic()],
    ["heading", FaHeading, translate("MARKDOWN_HEADING"), (c) => c.toggleHeading({ level: 1 })],
    ["bulletList", FaListUl, translate("MARKDOWN_BULLET_LIST"), (c) => c.toggleBulletList()],
    ["orderedList", FaListOl, translate("MARKDOWN_NUMBERED_LIST"), (c) => c.toggleOrderedList()],
  ];

  const active = useEditorState({
    editor,
    selector: ({ editor }) => items.map(([name]) => editor.isActive(name)),
  });

  return (
    <div css={{ display: "flex", gap: 2, marginBottom: 6 }}>
      {items.map(([, Icon, label, toggle], i) => (
        <button
          key={label}
          type="button"
          title={label}
          aria-label={label}
          aria-pressed={active[i]}
          // Without this the button steals focus and the command loses the selection
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => toggle(editor.chain().focus()).run()}
          css={{
            display: "flex",
            padding: 4,
            borderRadius: 4,
            fontSize: "var(--swing-font-xs)",
            color: active[i] ? "var(--swing-blue500)" : "inherit",
            backgroundColor: active[i] ? "var(--swing-blue200)" : "transparent",
          }}
        >
          <Icon />
        </button>
      ))}
    </div>
  );
}
